import { apiFetch, type ApiError } from '../client.js';
import type { paths } from '../generated.js';

export interface MeResponse {
  readonly user: {
    id: string;
    email: string;
    displayName: string | null;
    isStaff: boolean;
  };
  readonly org?: { id: string; name: string } | null;
  readonly orgRole?: 'owner' | 'admin' | 'member' | null;
  readonly memberships: ReadonlyArray<{
    projectId: string;
    projectName: string;
    role: 'viewer' | 'reviewer' | 'admin' | 'owner';
  }>;
}

export async function fetchMe(): Promise<MeResponse> {
  // The session probe: a 401 here means "anonymous", which the auth store's
  // bootstrap() handles by clearing state. Suppress the global on401 redirect so
  // this probe never bounces an anonymous visitor off a public page (e.g. the
  // admin-generated /set-password/:token link) to /login.
  return apiFetch('/v1/auth/me', {
    method: 'GET',
    suppressAuthRedirect: true,
  }) as Promise<MeResponse>;
}

// The 200 envelope of PATCH /v1/auth/me: the same `user` object GET nests, so the
// shape is reused rather than redeclared (HUBL-072 ticket 03).
export interface UpdateMeResponse {
  readonly user: MeResponse['user'];
}

// Renames the caller. Deliberately does NOT pass `suppressAuthRedirect`: unlike
// fetchMe's anonymous probe, a 401 on this write means the session really has
// expired and the global bounce to /login is the right answer. Returns the
// response's `user` so the caller can hand it straight to the auth store — the
// route answers with it precisely so no `me` refetch is needed. An ApiError is
// the caller's to render.
export async function updateDisplayName(input: {
  displayName: string;
}): Promise<MeResponse['user']> {
  const res = (await apiFetch('/v1/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(input),
  })) as UpdateMeResponse;
  return res.user;
}

export async function login(input: { email: string; password: string }): Promise<void> {
  await apiFetch('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function logout(): Promise<void> {
  await apiFetch('/v1/auth/logout', { method: 'POST' });
}

// Session-authed one-shot for a just-accepted invitee whose `password_hash` is
// still null: the accept call minted the session, this call uses it to set the
// password (204). The server floor is min(8); the caller (InvitationAccept.vue)
// enforces a stricter min(12). A 409 means the password was already set — the
// caller surfaces the error rather than silently retrying.
export async function setPassword(input: { password: string }): Promise<void> {
  await apiFetch('/v1/auth/set-password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Public. The server answers 202 `{ status: 'accepted' }` for a known, an unknown,
// and a disabled address alike — the response carries no outcome-varying field BY
// DESIGN, so there is nothing here to return and callers have nothing to branch on
// (SD-065 ticket 04; the non-enumeration invariant). A 429 is the one honest signal
// and leaks nothing: the per-email limiter is keyed on whatever was submitted,
// account or not.
export async function requestPasswordReset(input: { email: string }): Promise<void> {
  await apiFetch('/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// Public. Consumes the emailed token and sets the new password (200). Mints NO
// session — unlike invitation accept — so the caller sends the user to /login. A
// closed token is a 400 `ApiError` whose `details.code` is `invalid | expired |
// used`; the caller branches on that to offer a fresh link.
export async function completePasswordReset(input: {
  token: string;
  password: string;
}): Promise<void> {
  await apiFetch('/v1/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type { ApiError };

type _Validate =
  paths['/v1/auth/me']['get']['responses']['200']['content']['application/json'] extends MeResponse
    ? true
    : false;
const _validate: _Validate = true;
void _validate;

type _ValidateUpdateMeBody =
  paths['/v1/auth/me']['patch']['requestBody']['content']['application/json'] extends {
    displayName: string;
  }
    ? true
    : false;
const _validateUpdateMeBody: _ValidateUpdateMeBody = true;
void _validateUpdateMeBody;

type _ValidateUpdateMeResponse =
  paths['/v1/auth/me']['patch']['responses']['200']['content']['application/json'] extends UpdateMeResponse
    ? true
    : false;
const _validateUpdateMeResponse: _ValidateUpdateMeResponse = true;
void _validateUpdateMeResponse;

type _ValidateSetPassword =
  paths['/v1/auth/set-password']['post']['requestBody']['content']['application/json'] extends {
    password: string;
  }
    ? true
    : false;
const _validateSetPassword: _ValidateSetPassword = true;
void _validateSetPassword;

type _ValidateForgotPassword =
  paths['/v1/auth/forgot-password']['post']['requestBody']['content']['application/json'] extends {
    email: string;
  }
    ? true
    : false;
const _validateForgotPassword: _ValidateForgotPassword = true;
void _validateForgotPassword;

type _ValidateResetPassword =
  paths['/v1/auth/reset-password']['post']['requestBody']['content']['application/json'] extends {
    token: string;
    password: string;
  }
    ? true
    : false;
const _validateResetPassword: _ValidateResetPassword = true;
void _validateResetPassword;

// The success shape is asserted even though the wrapper returns void: `status: 'ok'`
// is the whole 200 body, so this is what would catch the route changing its mind.
type _ValidateResetPasswordResponse =
  paths['/v1/auth/reset-password']['post']['responses']['200']['content']['application/json'] extends {
    status: 'ok';
  }
    ? true
    : false;
const _validateResetPasswordResponse: _ValidateResetPasswordResponse = true;
void _validateResetPasswordResponse;
